import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import React from 'react';
import { tv } from 'tailwind-variants';
import cn from '@/utils/cn';
import { useSPAThemeScope } from './SPAThemeScope';

// AIDEV-NOTE: SPA-scoped Radix DropdownMenu for "click trigger → list of actions". Use this rather
// than SPAPopover for action menus: a Popover has no menu semantics, so roving focus, typeahead,
// Home/End and role="menuitem" all have to be hand-rolled, and consumers ended up reaching for
// onOpenAutoFocus to move focus onto the first item themselves.
//
// Unlike ui/Dropdown's TCIDropdown this uses Radix's own Item. TCIDropdown renders plain divs
// because Radix's Item blurs on pointer-leave, which kills a TCITooltip wrapping a menu item — no
// SPA menu puts a tooltip inside an item, so that trade does not apply here and we keep Radix's
// keyboard handling for free.
//
// Composed onto a local wrapper, never Object.assign onto the radix export — mutating the shared
// export collides with anything else built on the same primitive (see SPAPopover for that bug).

const contentVariants = tv({
  base: [
    'tw-z-[9999] tw-min-w-[8rem] tw-box-border',
    'tw-bg-spa-bg-secondary tw-border tw-border-solid tw-border-spa-border',
    'tw-rounded-lg tw-shadow-lg tw-p-1 tw-outline-none',
    'data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 data-[state=open]:tw-zoom-in-95',
    'data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95',
    'data-[side=bottom]:tw-slide-in-from-top-1 data-[side=top]:tw-slide-in-from-bottom-1',
    'data-[side=left]:tw-slide-in-from-right-1 data-[side=right]:tw-slide-in-from-left-1',
  ],
});

// AIDEV-NOTE: The highlight ring keys off data-[highlighted], which Radix sets for both keyboard
// and pointer focus — the same approach as ui/Dropdown, so the two families behave alike. The
// no-underline rules exist because the global a:hover/a:focus underline otherwise leaks into rows
// whose item is (or wraps) an anchor.
const itemVariants = tv({
  base: [
    'tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-box-border',
    'tw-text-sm tw-text-left tw-rounded tw-text-spa-primary',
    'tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-outline-none',
    'tw-no-underline hover:tw-no-underline focus:tw-no-underline',
    '[&_a]:tw-text-inherit [&_a]:tw-no-underline [&_a:hover]:tw-no-underline',
    'data-[highlighted]:tw-bg-spa-bg-hover data-[highlighted]:tw-text-spa-on-bg',
    'data-[highlighted]:tw-ring-2 data-[highlighted]:tw-ring-inset data-[highlighted]:tw-ring-spa-accent',
    'data-[disabled]:tw-text-spa-tertiary data-[disabled]:tw-cursor-not-allowed',
  ],
});

// AIDEV-NOTE: Focus an item on every open, matching ui/Dropdown's TCIDropdown. Radix's own
// behaviour highlights nothing when the menu is opened by pointer — defensible, but the teacher-side
// menus deliberately highlight on any open, and two menu families behaving differently is worse than
// either rule on its own. Consumers can still preventDefault in their own onOpenAutoFocus.
//
// Prefers the item marked current (aria-current, or aria-checked for a RadioGroup) so a menu opens
// on the active choice rather than the top of the list.
const CURRENT_ITEM_SELECTOR = '[aria-current="true"], [aria-checked="true"]';

const queryMenuItems = (root: HTMLElement | null) => (
  root ? Array.from(root.querySelectorAll<HTMLElement>('[role^="menuitem"]')) : []
);

// AIDEV-NOTE: @radix-ui/react-menu types onOpenAutoFocus on its content props and handles it at
// runtime, but the react-dropdown-menu re-export drops it from its public type. Declared here, and
// the primitive is cast once below, so both the wrapper and the JSX type-check.
interface DropdownContentProps {
  onOpenAutoFocus?: (event: Event) => void;
}

const MenuContent = DropdownMenuPrimitive.Content as React.ForwardRefExoticComponent<
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
& DropdownContentProps
& React.RefAttributes<HTMLDivElement>
>;

const DropdownContent = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Content>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & DropdownContentProps
>(({ align = 'start', className, side = 'bottom', sideOffset = 4, onOpenAutoFocus, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  // Portalled into document.body, so a legacy mount's pinned theme class has to travel with it.
  const themeScope = useSPAThemeScope();

  const setRefs = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  const handleOpenAutoFocus = (event: Event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented) return;

    event.preventDefault();
    // Defer a frame so Content and its items have rendered before we move focus.
    requestAnimationFrame(() => {
      const items = queryMenuItems(contentRef.current);
      const current = items.findIndex(item => item.matches(CURRENT_ITEM_SELECTOR));
      items[current >= 0 ? current : 0]?.focus();
    });
  };

  return (
    <DropdownMenuPrimitive.Portal>
      <MenuContent
        ref={setRefs}
        align={align}
        className={cn(contentVariants(), themeScope, className)}
        side={side}
        sideOffset={sideOffset}
        onOpenAutoFocus={handleOpenAutoFocus}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

DropdownContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownItem = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Item>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item ref={ref} className={cn(itemVariants(), className)} {...props} />
));

DropdownItem.displayName = DropdownMenuPrimitive.Item.displayName;

// AIDEV-NOTE: For mutually-exclusive choices (the theme picker). Gives role="menuitemradio" and
// aria-checked, which styling the selected row alone does not convey to a screen reader.
const DropdownRadioItem = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(itemVariants(), 'data-[state=checked]:tw-text-spa-high-contrast', className)}
    {...props}
  />
));

DropdownRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownSeparator = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('tw-my-1 tw-h-px tw-bg-spa-border', className)}
    {...props}
  />
));

DropdownSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

// A group heading inside the menu (the unit above its lessons): not focusable, not an item.
const DropdownLabel = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Label>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'tw-px-3 tw-pb-1 tw-pt-2 tw-text-[0.6875rem] tw-font-bold tw-uppercase tw-tracking-wider tw-text-spa-tertiary',
      className,
    )}
    {...props}
  />
));

DropdownLabel.displayName = DropdownMenuPrimitive.Label.displayName;

// AIDEV-NOTE: modal={false} matches ui/Dropdown's TCIDropdown. Radix's default modal={true} traps
// Tab and, to make the page inert, scroll-locks it and sets body pointer-events:none — state that is
// only released when the menu unmounts, which Presence defers until an animationend arrives. A menu
// does not need the page inert, and not taking the lock means a missed animationend cannot strand it.
const SPADropdownRoot = ({
  modal = false,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) => (
  <DropdownMenuPrimitive.Root modal={modal} {...props} />
);

const SPADropdown = Object.assign(SPADropdownRoot, {
  Content: DropdownContent,
  Item: DropdownItem,
  Label: DropdownLabel,
  RadioGroup: DropdownMenuPrimitive.RadioGroup,
  RadioItem: DropdownRadioItem,
  Separator: DropdownSeparator,
  Trigger: DropdownMenuPrimitive.Trigger,
});

export default SPADropdown;
